import { useState } from "react";
import { postKmsData, translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { BulkActionProps } from "@kaltura/mediaspace-shared-selectable-media-list";

export interface BulkDeleteActionProps extends BulkActionProps {
    apiUrl: string;
}

/**
 * Hook for handling bulk delete actions.
 * 1. Opens a confirmation modal.
 * 2. Sends a request to the backend to delete the selected entries.
 * 3. Reloads the media list after successful deletion.
 * 4. Closes the confirmation modal.
 * 5. refetches the media list.
 *
 * @param entryIds - The IDs of the entries to be deleted.
 * @param reloadMedia - A function to reload the media list.
 * @param apiUrl - The URL of the API endpoint for deleting entries.
 * @returns An object containing the selected count, open confirmation state, processing state, handle click function, handle delete function, and close confirmation function.
 */
export const useBulkDelete = ({ entryIds, reloadMedia, apiUrl }: BulkDeleteActionProps) => {
    const selectedCount = entryIds.length;
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        setOpenConfirmation(false);
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics("Bulk delete items", ButtonClickAnalyticsType.DELETE);
    };

    const handleDelete = async () => {
        setProcessing(true);
        try {
            const response = await postKmsData(apiUrl, { ids: entryIds });
            if (response.success) {
                showToast({
                    severity: "success",
                    message: translatePlural(
                        "%1 item has been permanently deleted.",
                        "%1 items have been permanently deleted.",
                        [selectedCount]
                    ),
                });
                closeConfirmation();
                reloadMedia?.();
            }
            else {
                showToast({
                    severity: "error",
                    message: translate("Failed to permanently delete items."),
                });
            }
        }
        catch (e: unknown) {
            console.error("Failed to permanently delete entries.", e);
            showToast({
                severity: "error",
                message: translate("Failed to permanently delete items."),
            });
        }
        finally {
            setProcessing(false);
        }
    };

    return { selectedCount, openConfirmation, processing, handleClick, handleDelete, closeConfirmation };
};
